import { useState, useRef, useEffect } from 'react';
import { BadgeCheck, X, ShieldCheck, FileCheck2, Building2, User } from 'lucide-react';

// Verified badge with a hover/tap popover explaining what verification means.
// Props: size (pill sizing), variant ('inline' compact for cards, 'large' for profile)
export default function VerifiedBadge({ variant = 'inline' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const pillClasses = variant === 'large'
    ? 'inline-flex items-center gap-1 rounded-full bg-[#2A9D8F]/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#2A9D8F]'
    : 'inline-flex items-center gap-1 rounded-full bg-[#2A9D8F]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#2A9D8F]';

  return (
    <span ref={ref} className="relative inline-block" data-testid="verified-badge">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        onMouseEnter={() => setOpen(true)}
        className={`${pillClasses} cursor-help transition-colors hover:bg-[#2A9D8F]/20`}
        data-testid="verified-badge-trigger"
      >
        <BadgeCheck size={variant === 'large' ? 12 : 12} /> Verified
      </button>

      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          className="absolute left-1/2 top-full z-30 mt-2 w-[300px] -translate-x-1/2 border border-[#1C1B1A] bg-white p-5 shadow-[4px_4px_0_#1C1B1A] normal-case tracking-normal"
          data-testid="verified-popover"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#2A9D8F]">How we verify</div>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
              className="text-[#8A8988] hover:text-[#1C1B1A]"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-[#1C1B1A]">
            Every Real Tradies tick is checked by a real person. We&apos;re not paid for it — verification is free forever.
          </p>
          <ul className="mt-3 space-y-2 text-xs text-[#5C5B5A]">
            <li className="flex gap-2"><FileCheck2 size={14} className="mt-0.5 shrink-0 text-[#2A9D8F]" /> Trade license or industry ticket sighted</li>
            <li className="flex gap-2"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#2A9D8F]" /> Public liability insurance current</li>
            <li className="flex gap-2"><Building2 size={14} className="mt-0.5 shrink-0 text-[#2A9D8F]" /> ABN checked against the Australian Business Register</li>
            <li className="flex gap-2"><User size={14} className="mt-0.5 shrink-0 text-[#2A9D8F]" /> At least one past customer reference confirmed</li>
          </ul>
          <div className="mt-3 border-t border-[#E5E4E0] pt-2 text-[10px] uppercase tracking-[0.15em] text-[#8A8988]">
            No paid rankings · No lead auction
          </div>
        </div>
      )}
    </span>
  );
}
